import React, { Fragment, useEffect, useRef, useState } from "react";
import { Menu, MenuButton, MenuItems, Transition } from "@headlessui/react";
import { usePopper } from "react-popper";
import Checkbox from "./Checkbox";
import NoDataFound from "../common/NodataFound";
import { classNames } from "../../helpers/classNames";
import Search from "./Search";
import Indentification from "../elements/Indentification";

const MultiselectDropdown = ({
  xPlacement = "bottom",
  dropdownData = [],
  setDropdownData = () => {},
  type = "select", // select||"tag"
  hasLogo = false,
  isCheckbox = false,
  isSearch = false,
  label,
  values = [],
  required,
  labelClasses,
  dropdownButtonClass,
  buttonLabel,
  buttonLabelClasses,
  buttonIcon,
  buttonIconPosition = "left",
  buttonArrowIcon = "fa-regular fa-chevron-down",
  buttonArrowIconClasses,
  dropdownContainer,
  searchValue,
  buttonPlaceholder,
  onChange = () => {},
  search = () => {},
  onScroll = () => {},
  loading = false,
  errorType,
  errorText,
  ...props
}) => {
  const divRef = useRef(null);
  const popperElRef = useRef(null);
  const [targetElement, setTargetElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [list, setList] = useState([]);

  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: xPlacement,
    modifiers: [{ name: "offset", options: { offset: [0, 5] } }],
  });

  useEffect(() => {
    if (Array.isArray(dropdownData)) {
      setList(
        dropdownData.map((item) => ({
          ...item,
          checked: values.includes(item.value),
        }))
      );
    }
  }, [JSON.stringify(dropdownData), JSON.stringify(values)]);

  const handleItemClick = (item) => {
    let updatedList;
    if (item.value === "all") {
      const allChecked = list.every((i) => i.checked);
      updatedList = list.map((i) => ({ ...i, checked: !allChecked }));
    } else {
      updatedList = list.map((i) =>
        i.value === item.value ? { ...i, checked: !i.checked } : i
      );
    }
    setList(updatedList);
    const selectedValues = updatedList
      .filter((i) => i.checked)
      .map((i) => i.value);
    onChange(selectedValues);
  };

  return (
    <div className="w-full relative space-y-1">
      {label && (
        <div
          className={classNames(
            "text-xs font-medium text-text-disabled mb-1  mb-1 ",
            labelClasses
          )}
        >
          {label}
          {required && (
            <span className="text-[8px] text-red-500 ml-1 absolute top-[-2px]">
              <i className="fa-solid fa-asterisk"></i>
            </span>
          )}
        </div>
      )}
      <Menu as="div" className="w-full" ref={divRef}>
        {({ open }) => (
          <>
            <div className="relative w-full">
              <MenuButton
                ref={setTargetElement}
                className={classNames(
                  "relative w-full h-10 flex items-center justify-between gap-2 pr-2 pl-3 rounded-md bg-white border px-3  text-sm text-gable-green !ring-0 !outline-0 transition-all duration-200",
                  type === "select"
                    ? "h-10 space-x-2"
                    : "min-h-10 h-fit gap-1 flex-wrap",
                  dropdownButtonClass,
                  open ? "border-shades-07" : "border-shades-05/50"
                )}
              >
                {type === "select" && (
                  <>
                    {buttonIconPosition === "left" && buttonIcon && (
                      <i className={classNames("fa-fw", buttonIcon)}></i>
                    )}
                    <div
                      className={classNames(
                        " whitespace-nowrap truncate text-text-disabled",

                        buttonLabelClasses
                      )}
                    >
                      {list?.filter((item) => item?.checked)?.length > 0 ? (
                        <>
                          Selected{" "}
                          <span className="text-text-disabled">
                            ({list.filter((item) => item?.checked).length})
                          </span>
                        </>
                      ) : (
                        buttonPlaceholder || "Select"
                      )}
                    </div>

                    {buttonIconPosition === "right" && buttonIcon && (
                      <i className={classNames("fa-fw", buttonIcon)}></i>
                    )}
                  </>
                )}
                {type === "tag" && (
                  <>
                    {list?.filter((item) => item?.checked)?.length > 0 ? (
                      list
                        ?.filter((item) => item?.checked)
                        ?.map((item, i) => (
                          <div
                            key={item?.value || i?.toString}
                            className="text-xs divide-x py-[1px] border border-transparent bg-chestnut-rose text-white rounded-sm"
                          >
                            <span className="px-1">{item?.name}</span>
                            <span onClick={(e) => e.stopPropagation()}>
                              <span
                                onClick={() => {
                                  handleItemClick(item);
                                }}
                                className="px-1"
                              >
                                <i className="fa-fw fa-regular fa-xmark"></i>
                              </span>
                            </span>
                          </div>
                        ))
                    ) : (
                      <span className="text-[13px] text-slate-500 font-medium whitespace-nowrap truncate">
                        {buttonPlaceholder || "Select"}
                      </span>
                    )}
                  </>
                )}
                {buttonArrowIcon && (
                  <div
                    className={classNames(
                      "absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400",
                      open ? "-rotate-180" : "",
                      buttonArrowIconClasses
                    )}
                  >
                    <i className={classNames("fa-fw", buttonArrowIcon)}></i>
                  </div>
                )}
              </MenuButton>
            </div>

            <Transition
              as={Fragment}
              className="z-50"
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
              beforeEnter={() => setPopperElement(popperElRef.current)}
              afterLeave={() => setPopperElement(null)}
            >
              <MenuItems
                ref={popperElRef}
                style={{
                  ...styles.popper,
                  ...(divRef?.current?.clientWidth && {
                    width: divRef?.current?.clientWidth,
                  }),
                }}
                {...attributes.popper}
                className={classNames(
                  "bg-white shadow-lg rounded-md border py-1",
                  dropdownContainer
                )}
              >
                {isSearch && (
                  <div className="py-2 px-3">
                    <Search
                      searchValue={searchValue}
                      placeholder={props?.placeholder || "Search"}
                      search={search}
                      onKeyDown={(e) => {
                        if (e.key === " ") {
                          e.preventDefault();
                          const { selectionStart, selectionEnd, value } =
                            e.target;
                          const newValue =
                            value.slice(0, selectionStart) +
                            " " +
                            value.slice(selectionEnd);
                          search(newValue);
                          requestAnimationFrame(() => {
                            e.target.selectionStart = e.target.selectionEnd =
                              selectionStart + 1;
                          });
                        }
                      }}
                    />
                  </div>
                )}

                <div
                  className="max-h-64 overflow-auto scrollbar divide-y divide-gray-200"
                  onScroll={onScroll}
                >
                  {list.length > 0 ? (
                    list.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => handleItemClick(item)}
                        className="w-full flex gap-2 items-center select-none p-2 text-xs leading-tight cursor-pointer transition-all duration-200 text-slate-500"
                        role="menuitem"
                      >
                        <span className="flex h-5 w-5 items-center justify-center">
                          {item?.checked && (
                            <i className="fa-fw fa-regular text-sm fa-check text-chestnut-rose"></i>
                          )}
                        </span>
                        {hasLogo && (
                          <Indentification
                            image={item.logo}
                            alt={item.name}
                            initial={item.initials}
                            className="w-8 h-8 border rounded-md"
                          />
                        )}
                        <div className="whitespace-nowrap">{item.name}</div>
                      </button>
                    ))
                  ) : !loading ? (
                    <NoDataFound
                      title={
                        <div className="text-base font-medium text-shades-07">
                          No data found
                        </div>
                      }
                    />
                  ) : (
                    <div className="px-1">Loading...</div>
                  )}
                </div>
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>
      {errorType && errorType === "danger" && (
        <div className="text-xs font-medium text-red-600">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "warning" && (
        <div className="text-xs font-medium text-amber-500">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "success" && (
        <div className="text-xs font-medium text-green-600">
          <i className="fa-regular fa-fw fa-circle-check text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "info" && (
        <div className="text-xs font-medium text-sky-600">
          <i className="fa-regular fa-fw fa-circle-info text-sm mr-1"></i>
          {errorText}
        </div>
      )}
    </div>
  );
};

export default MultiselectDropdown;
